<template>
  <Teleport to="body">
    <div class="highlight-search" @click="handleOverlayClick" @keydown.esc="close">
      <div class="highlight-search__dialog" role="dialog" aria-modal="true" :aria-label="$t('plan_trail.route_highlights.search_title')" @click.stop>
        <header class="highlight-search__header">
          <div>
            <h3>{{ $t('plan_trail.route_highlights.search_title') }}</h3>
            <p class="highlight-search__subtitle">{{ dayTitle }}</p>
          </div>
          <button class="highlight-search__close" type="button" :aria-label="$t('shared.close')" @click="close">
            <span class="material-icons" aria-hidden="true">close</span>
          </button>
        </header>

        <div class="highlight-search__controls">
          <input
            ref="input"
            v-model="query"
            class="highlight-search__input"
            type="search"
            :placeholder="$t('plan_trail.route_highlights.search_placeholder')"
            :aria-label="$t('plan_trail.route_highlights.search_placeholder')"
          />
          <div class="highlight-search__groups">
            <button
              v-for="option in groups"
              :key="option.key"
              type="button"
              class="highlight-search__group"
              :class="{ 'highlight-search__group--active': group === option.key }"
              @click="group = option.key"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="highlight-search__results">
          <p v-if="loading" class="highlight-search__hint">{{ $t('plan_trail.route_highlights.searching') }}</p>
          <p v-else-if="!candidates.length && !googlePlaces?.length" class="highlight-search__hint">{{ $t('plan_trail.route_highlights.no_results') }}</p>
          <div v-for="candidate in shown" :key="`${candidate.highlight.category}-${candidate.highlight.id}`" class="highlight-search__result">
            <button type="button" class="highlight-search__place" @click="showOnMap(candidate.highlight)">
              <img class="highlight-search__icon" :src="iconFor(candidate.highlight.type)" alt="" />
              <span class="highlight-search__text">
                <span class="highlight-search__name">{{ candidate.highlight.name }}</span>
                <small class="highlight-search__meta">
                  {{ placeTypeLabel(candidate.highlight.type) }} · {{ formatDistance(candidate.alongM) }}
                  <template v-if="candidate.offRouteM >= 50"> · {{ $t('plan_trail.route_highlights.off_route', { distance: formatDistance(candidate.offRouteM) }) }}</template>
                </small>
              </span>
            </button>
            <span v-if="candidate.added" class="highlight-search__added">
              <span class="material-icons" aria-hidden="true">check</span>{{ $t('plan_trail.route_highlights.added') }}
            </span>
            <button v-else type="button" class="highlight-search__add" @click="add(candidate.highlight)">
              <span class="material-icons" aria-hidden="true">add</span>{{ $t('plan_trail.route_highlights.add') }}
            </button>
          </div>
          <button v-if="candidates.length > shown.length" type="button" class="highlight-search__more" @click="shownCount += PAGE_SIZE">
            {{ $t('plan_trail.route_highlights.show_more_results', { count: Math.min(PAGE_SIZE, candidates.length - shown.length), total: candidates.length - shown.length }) }}
          </button>

          <!-- None of our places is the one they typed: try Google Maps. -->
          <button v-if="offerGoogle" type="button" class="highlight-search__google" @click="searchGoogle">
            <span class="material-icons" aria-hidden="true">travel_explore</span>
            {{ $t('plan_trail.place_search.search_google', { query: query.trim() }) }}
          </button>
          <p v-if="googleLoading" class="highlight-search__hint">{{ $t('plan_trail.place_search.google_searching') }}</p>
          <p v-else-if="googleFailed" class="highlight-search__hint">{{ $t('plan_trail.place_search.google_failed') }}</p>
          <p v-else-if="googlePlaces && !googlePlaces.length" class="highlight-search__hint">{{ $t('plan_trail.place_search.google_none') }}</p>
          <template v-if="googlePlaces && googlePlaces.length">
            <p class="highlight-search__group-title">{{ $t('plan_trail.place_search.google_maps') }}</p>
            <div v-for="entry in googleEntries" :key="entry.place.placeId" class="highlight-search__result">
              <button type="button" class="highlight-search__place" @click="showOnMap(entry.place)">
                <img class="highlight-search__icon" :src="iconFor(entry.place.type)" alt="" />
                <span class="highlight-search__text">
                  <span class="highlight-search__name">{{ entry.place.name }}</span>
                  <small class="highlight-search__meta">
                    <template v-if="adding === entry.place.placeId">{{ $t('plan_trail.place_search.adding') }}</template>
                    <template v-else>
                      {{ entry.place.category || placeTypeLabel(entry.place.type) }}
                      <template v-if="entry.offRouteM >= 50"> · {{ $t('plan_trail.route_highlights.off_route', { distance: formatDistance(entry.offRouteM) }) }}</template>
                    </template>
                  </small>
                </span>
              </button>
              <span v-if="entry.added" class="highlight-search__added">
                <span class="material-icons" aria-hidden="true">check</span>{{ $t('plan_trail.route_highlights.added') }}
              </span>
              <button v-else type="button" class="highlight-search__add" :disabled="!!adding" @click="addGoogle(entry.place)">
                <span class="material-icons" aria-hidden="true">add</span>{{ $t('plan_trail.route_highlights.add') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { i18n } from '../../../i18n';
import {
  HIGHLIGHT_ACCOMMODATION_TYPES,
  HIGHLIGHT_AMENITY_TYPES,
  HIGHLIGHT_NAME_SEARCH_M,
  highlightCandidates,
  HighlightPlaceGroup,
  dayLine,
  isAlreadyHighlighted,
  placeHighlight,
} from '../services/route-highlights';
import { nearestAlongLine } from '../services/day-flags';
import { pointAtDistance } from '../utils/trailLine';
import { GooglePlace, importGooglePlace, rankGooglePlaces, searchGoogleMaps } from '../../../utils/services/GoogleMapsPlaces';
import { NearbyPlace, placeTypeLabel } from '../services/route-endpoints';
import { placesAlongLine } from '../services/nearby-places';
import { highlightIconUrl } from '../utils/highlightIcon';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';

/**
 * Places within reach of one route — sights, food, places to stay, practical
 * stops — each of which can be added to the plan as a highlight on the
 * route's day, linked back to the place.
 */

/** Results drawn at a time — "Show more" draws the next lot. */
const PAGE_SIZE = 60;

const props = defineProps<{ dayNumber: number }>();
const emit = defineEmits(['close']);
const store = useStore();

const input = ref<HTMLInputElement | null>(null);
const query = ref('');
const group = ref<HighlightPlaceGroup>('all');
const places = ref<NearbyPlace[]>([]);
const loading = ref(true);

// Literal keys, so they ship in the client's translations.
const groups = computed<Array<{ key: HighlightPlaceGroup; label: string }>>(() => [
  { key: 'all', label: i18n.global.t('plan_trail.route_highlights.groups.all') },
  { key: 'sights', label: i18n.global.t('plan_trail.route_highlights.groups.sights') },
  { key: 'food', label: i18n.global.t('plan_trail.route_highlights.groups.food') },
  { key: 'stay', label: i18n.global.t('plan_trail.route_highlights.groups.stay') },
  { key: 'practical', label: i18n.global.t('plan_trail.route_highlights.groups.practical') },
]);

const dayTitle = computed(() => store.state.planTrail?.dayInfo?.[props.dayNumber]?.name
  || i18n.global.t('plan_trail.route_highlights.day_title', { day: props.dayNumber }));
const line = computed(() => dayLine(store.state.planTrail?.routes ?? {}, props.dayNumber));

// Google Maps, when none of our places is the one wanted: searched around the
// middle of the day, on request (each search is a SerpApi call).
const googlePlaces = ref<GooglePlace[] | null>(null);
const googleLoading = ref(false);
const googleFailed = ref(false);
/** The Google place being added, once its Add is pressed. */
const adding = ref<string | null>(null);

const dayMiddle = computed<[number, number] | null>(() => {
  if (!line.value) return null;
  const { coordinates, cumDist } = line.value;
  const point = pointAtDistance(coordinates, cumDist, cumDist[cumDist.length - 1] / 2);
  return [point[0], point[1]];
});

const offerGoogle = computed(() => query.value.trim().length >= 3
  && !!dayMiddle.value
  && !loading.value
  && candidates.value.length === 0
  && googlePlaces.value === null
  && !googleLoading.value
  && !googleFailed.value);

const googleEntries = computed(() => (googlePlaces.value ?? []).map(place => ({
  place,
  offRouteM: line.value ? nearestAlongLine(line.value, place.coordinates).offLineM : 0,
  added: isAlreadyHighlighted({ id: place.placeId, category: 'Accommodation', type: place.type, name: place.name, coordinates: place.coordinates },
    store.state.planTrail?.hikeHighlights ?? []),
})));

watch(query, () => {
  googlePlaces.value = null;
  googleFailed.value = false;
  shownCount.value = PAGE_SIZE;
});
watch(group, () => { shownCount.value = PAGE_SIZE; });

const candidates = computed(() => (line.value
  ? highlightCandidates(places.value, line.value, {
    query: query.value,
    group: group.value,
    highlights: store.state.planTrail?.hikeHighlights ?? [],
  })
  : []));
const shownCount = ref(PAGE_SIZE);
const shown = computed(() => candidates.value.slice(0, shownCount.value));

onMounted(async () => {
  await nextTick();
  input.value?.focus();
  if (!line.value) {
    loading.value = false;
    return;
  }
  try {
    places.value = await placesAlongLine(
      line.value.coordinates,
      { accommodationTypes: HIGHLIGHT_ACCOMMODATION_TYPES, amenityTypes: HIGHLIGHT_AMENITY_TYPES },
      // Far enough for a search by name; browsing keeps to the nearer ones.
      HIGHLIGHT_NAME_SEARCH_M
    );
  } finally {
    loading.value = false;
  }
});

// The marker the map shows for it (see utils/highlightIcon.ts).
function iconFor(type: string): string {
  return highlightIconUrl(type);
}

function formatDistance(metres: number): string {
  return MeasurementPrefUtility.preference().smallUnitFormatter(metres, true);
}

async function searchGoogle() {
  if (!dayMiddle.value) return;
  const searched = query.value.trim();
  googleLoading.value = true;
  try {
    const found = await searchGoogleMaps(searched, dayMiddle.value, 12);
    if (query.value.trim() === searched) googlePlaces.value = rankGooglePlaces(found, dayMiddle.value);
  } catch {
    if (query.value.trim() === searched) googleFailed.value = true;
  } finally {
    googleLoading.value = false;
  }
}

/**
 * A Google Maps place is added to our own places first, so the highlight
 * links to it — or, if it can't be, becomes a highlight on its own.
 */
async function addGoogle(place: GooglePlace) {
  if (adding.value) return;
  adding.value = place.placeId;
  try {
    const ours = await importGooglePlace(place.placeId);
    if (ours) {
      add({ id: ours.id, category: ours.klass, type: ours.type, name: ours.name || place.name, coordinates: ours.coordinates });
    } else {
      await store.dispatch('planTrail/addHighlightForPick', { pick: { kind: 'google', place }, dayNumber: props.dayNumber });
    }
  } finally {
    adding.value = null;
  }
}

function showOnMap(place: { coordinates: [number, number] }) {
  const map = (window as any).map;
  map?.flyTo({ center: place.coordinates, zoom: Math.max(map.getZoom?.() ?? 0, 15), essential: true });
}

function add(place: NearbyPlace) {
  store.commit('planTrail/addHikeHighlight', placeHighlight(place, props.dayNumber));
  store.dispatch('planTrail/refreshHighlights');
}

function handleOverlayClick(event: MouseEvent) {
  if (event.target === event.currentTarget) close();
}

function close() {
  emit('close');
}

defineExpose({ add, addGoogle, searchGoogle });
</script>

<style scoped>
.highlight-search {
  position: fixed;
  inset: 0;
  /* Bootstrap's modal level — above the map's own buttons (1000). */
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.highlight-search__dialog {
  display: flex;
  flex-direction: column;
  width: 92%;
  max-width: 520px;
  max-height: 85vh;
  border-radius: 12px;
  background: var(--dark, var(--dark, #17181D));
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  color: #fff;
  overflow: hidden;
}

.highlight-search__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.1rem 1.25rem 0.5rem;
}

.highlight-search__header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.highlight-search__subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: #a1a4ad;
}

.highlight-search__close {
  padding: 0.25rem;
  border: none;
  background: none;
  color: #fff;
  cursor: pointer;
}

.highlight-search__controls {
  padding: 0.25rem 1.25rem 0.5rem;
}

.highlight-search__input {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: var(--gray-raised, #2A2C34);
  color: #fff;
  font-size: 0.85rem;
}

.highlight-search__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
}

.highlight-search__group {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: #dcdde3;
  font-size: 0.75rem;
  cursor: pointer;
}

.highlight-search__group--active {
  border-color: #fddf00;
  background: rgba(253, 223, 0, 0.1);
  color: #fddf00;
}

.highlight-search__results {
  flex: 1;
  min-height: 8rem;
  overflow-y: auto;
  padding: 0.25rem 0.75rem 1rem;
}

.highlight-search__result {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.15rem 0;
}

.highlight-search__place {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.35rem 0.5rem;
  border: none;
  border-radius: 8px;
  background: none;
  color: #fff;
  text-align: left;
  cursor: pointer;
}

.highlight-search__place:hover {
  background: rgba(255, 255, 255, 0.06);
}

.highlight-search__icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.highlight-search__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.highlight-search__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
}

.highlight-search__meta {
  color: #a1a4ad;
}

.highlight-search__add,
.highlight-search__added {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.highlight-search__add {
  border: 1px solid #fddf00;
  background: transparent;
  color: #fddf00;
  cursor: pointer;
}

.highlight-search__add:hover {
  background: rgba(253, 223, 0, 0.12);
}

.highlight-search__added {
  color: #a1a4ad;
}

.highlight-search__add .material-icons,
.highlight-search__added .material-icons {
  font-size: 14px;
}

.highlight-search__more {
  margin: 0.5rem;
  padding: 0;
  border: none;
  background: none;
  color: #fddf00;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.highlight-search__more:hover {
  text-decoration: underline;
}

.highlight-search__group-title {
  margin: 0.75rem 0.5rem 0.25rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a1a4ad;
}

.highlight-search__google {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin: 0.6rem 0.5rem 0;
  padding: 0.4rem 0.7rem;
  border-radius: 10px;
  border: 1px solid rgba(253, 223, 0, 0.35);
  background: rgba(253, 223, 0, 0.1);
  color: #fddf00;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
}

.highlight-search__google .material-icons {
  flex-shrink: 0;
  font-size: 16px;
}

.highlight-search__google:hover {
  background: rgba(253, 223, 0, 0.18);
}

.highlight-search__add:disabled {
  opacity: 0.5;
  cursor: progress;
}

.highlight-search__hint {
  margin: 0.75rem 0.5rem;
  font-size: 0.8rem;
  color: #a1a4ad;
}
</style>
